"use client"

import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { usePathname } from "next/navigation"
import { APP_B2B_ACCOUNT_OWNER, APP_B2B_OCI_ACCOUNT_OWNER, APP_B2B_OCI_USER } from "@/constants"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { AlignLeftIcon, SearchIcon } from "lucide-react"
import { useTranslations } from "next-intl"

import {
  canCatalogVisibilityDifferForChannel,
  getCatalogVisibilityMode,
  shouldResolveCatalogVisibility,
} from "@/lib/catalog-visibility"
import { BasketData } from "@/lib/intershop/interfaces/basket/basket"
import { UserData } from "@/lib/intershop/interfaces/user/user"
import { DEFAULT_MARKET_CHANNEL, type MarketChannel } from "@/lib/market/market-channel"
import { hasAnyRoleId } from "@/lib/role-helper"
import { Category } from "@/lib/storefront/category/category"
import type { CommonPageContext } from "@/lib/tracking/tracking"
import { cn } from "@/lib/utils"
import { MultilinkStoryblok, UbHeaderLinkStoryblok, UbHeaderStoryblok } from "@/components/storyblok/component-types"
import { HeaderAppSettings } from "@/components/storyblok/header/header-types"
import HeaderLogo from "@/components/storyblok/header/HeaderLogo"
import StoryblokAccountPopupClient from "@/components/storyblok/header/StoryblokAccountPopupClient"
import StoryblokBelgianLocaleSwitch from "@/components/storyblok/header/StoryblokBelgianLocaleSwitch"
import StoryblokMenuBar from "@/components/storyblok/header/StoryblokMenuBar"
import StoryblokPrivateCustomerToggle from "@/components/storyblok/header/StoryblokPrivateCustomerToggle"
import StoryblokSearchBox from "@/components/storyblok/header/StoryblokSearchBox"
import StoryblokServicePhone from "@/components/storyblok/header/StoryblokServicePhone"
import StoryblokShoppingBasketPopup from "@/components/storyblok/header/StoryblokShoppingBasketPopup"
import StoryblokSwissLocaleSwitch from "@/components/storyblok/header/StoryblokSwissLocaleSwitch"
import StoryblokValueProposition from "@/components/storyblok/header/StoryblokValueProposition"

export interface UbHeaderLinkStoryblokExtended extends UbHeaderLinkStoryblok {
  multilink?: MultilinkStoryblok
  label?: string
  countries_exclude?: string[]
}

interface UBHeaderProps {
  oci: boolean
  blok: UbHeaderStoryblok
  applicationSettings: HeaderAppSettings
  categories: Category[]
  navigation: UbHeaderLinkStoryblokExtended[]
  toolNavigation?: UbHeaderLinkStoryblokExtended[]
  userData?: UserData
  isB2BCustomer: boolean
  isAnonymous: boolean
  basket?: BasketData
  sbLocale: string
  isoLocale: string
  customerLogoUrl?: string
  channel?: MarketChannel
}

interface StorefrontContextResponse extends CommonPageContext {
  oci: boolean
  userData?: UserData
  customerLogoUrl?: string
}

declare global {
  interface Window {
    refreshMiniCart?: () => Promise<void>
  }
}

export default function UBHeaderClient({
  oci,
  blok,
  applicationSettings,
  categories,
  navigation,
  toolNavigation = [],
  userData,
  isB2BCustomer,
  isAnonymous,
  basket,
  sbLocale,
  isoLocale,
  customerLogoUrl,
  channel = DEFAULT_MARKET_CHANNEL,
}: UBHeaderProps) {
  const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false)
  const [isMobileSearchVisible, setIsMobileSearchVisible] = useState(false)
  const [effectiveOci, setEffectiveOci] = useState(oci)
  const [effectiveUserData, setEffectiveUserData] = useState<UserData | undefined>(userData)
  const [effectiveIsB2BCustomer, setEffectiveIsB2BCustomer] = useState<boolean | undefined>(undefined)
  const [effectiveIsAnonymous, setEffectiveIsAnonymous] = useState(isAnonymous)
  const [effectiveCustomerLogoUrl, setEffectiveCustomerLogoUrl] = useState(customerLogoUrl)
  const [contextLoaded, setContextLoaded] = useState(false)
  const [effectiveCategories, setEffectiveCategories] = useState(categories)
  const [categoriesLoading, setCategoriesLoading] = useState(
    canCatalogVisibilityDifferForChannel(getCatalogVisibilityMode(channel), channel)
  )
  const pathname = usePathname()
  const previousPathnameRef = useRef(pathname)

  const { setIsB2BCustomer, setIsOCI, setBasketData, setUserData } = useUbStorefrontState()

  const t = useTranslations("storefront")
  const effectiveIsB2BFallback = effectiveIsB2BCustomer ?? isB2BCustomer
  const useRebrushLayout = !!(blok as any).enable_rebrush_layout
  const showRebrushCustomerToggle = !effectiveOci && !effectiveUserData
  const disableAccountPopup =
    effectiveOci &&
    hasAnyRoleId(effectiveUserData?.roleIds, [APP_B2B_OCI_USER]) &&
    !hasAnyRoleId(effectiveUserData?.roleIds, [APP_B2B_ACCOUNT_OWNER, APP_B2B_OCI_ACCOUNT_OWNER])

  useEffect(() => {
    window.refreshMiniCart = async () => {
      const response = await fetch(`/api/storefront/basket?sbLocale=${encodeURIComponent(sbLocale)}`, {
        cache: "no-store",
      })

      if (!response.ok) {
        return
      }

      const basketData = (await response.json()) as BasketData | null | undefined
      setBasketData(basketData ?? undefined)
    }

    return () => {
      delete window.refreshMiniCart
    }
  }, [sbLocale, setBasketData])

  useLayoutEffect(() => {
    if (effectiveIsB2BCustomer === undefined) {
      return
    }

    setIsB2BCustomer(effectiveIsB2BCustomer)
    setIsOCI(effectiveOci)
  }, [setIsB2BCustomer, effectiveIsB2BCustomer, setIsOCI, effectiveOci])

  useEffect(() => {
    const controller = new AbortController()
    const contextSearchParams = new URLSearchParams()
    contextSearchParams.set("sbLocale", sbLocale)
    contextSearchParams.set("channel", channel)

    fetch(`/api/storefront/context?${contextSearchParams.toString()}`, {
      cache: "no-store",
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : undefined))
      .then((context: StorefrontContextResponse | undefined) => {
        if (!context) {
          setContextLoaded(true)
          return
        }

        setEffectiveOci(context.oci)
        setEffectiveIsB2BCustomer(context.isB2B)
        setEffectiveIsAnonymous(context.isAnonymous)
        setEffectiveUserData(context.userData)
        setEffectiveCustomerLogoUrl(context.customerLogoUrl)
        setUserData(context.userData)
        setContextLoaded(true)
      })
      .catch((error) => {
        if (error?.name !== "AbortError") {
          console.error("Could not load storefront context for header", error)
        }
        setContextLoaded(true)
      })

    return () => controller.abort()
  }, [channel, sbLocale, setUserData])

  useEffect(() => {
    const effectiveChannel: MarketChannel = effectiveOci ? "oci" : channel
    const catalogVisibilityMode = getCatalogVisibilityMode(effectiveChannel)

    if (!canCatalogVisibilityDifferForChannel(catalogVisibilityMode, effectiveChannel)) {
      setEffectiveCategories(categories)
      setCategoriesLoading(false)
      return
    }

    if (!contextLoaded) {
      setCategoriesLoading(true)
      return
    }

    const shouldResolve = shouldResolveCatalogVisibility({
      mode: catalogVisibilityMode,
      channel: effectiveChannel,
      isAuthenticated: !effectiveIsAnonymous,
    })

    if (!shouldResolve) {
      setEffectiveCategories(categories)
      setCategoriesLoading(false)
      return
    }

    const controller = new AbortController()
    const apiSearchParams = new URLSearchParams()
    apiSearchParams.set("sbLocale", sbLocale)
    apiSearchParams.set("channel", effectiveChannel)
    setCategoriesLoading(true)

    fetch(`/api/storefront/categories/tree?${apiSearchParams.toString()}`, {
      cache: "no-store",
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : undefined))
      .then((response: { categories?: Category[] } | undefined) => {
        setEffectiveCategories(response?.categories ?? categories)
      })
      .catch((error) => {
        if (error?.name !== "AbortError") {
          console.error("Could not load personalized categories for header", error)
        }
        setEffectiveCategories(categories)
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setCategoriesLoading(false)
        }
      })

    return () => controller.abort()
  }, [categories, channel, contextLoaded, effectiveIsAnonymous, effectiveOci, sbLocale])

  useEffect(() => {
    if (basket) {
      setBasketData(basket)
      return
    }

    const controller = new AbortController()

    fetch(`/api/storefront/basket?sbLocale=${encodeURIComponent(sbLocale)}`, {
      cache: "no-store",
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : undefined))
      .then((basketData: BasketData | null | undefined) => {
        setBasketData(basketData ?? undefined)
      })
      .catch((error) => {
        if (error?.name !== "AbortError") {
          console.error("Could not load basket for header", error)
        }
      })

    return () => controller.abort()
  }, [basket, sbLocale, setBasketData])

  const onMobileMenuClick = () => {
    const localIsMobileMenuVisible = !isMobileMenuVisible
    setIsMobileMenuVisible(localIsMobileMenuVisible)
    document.body.style.overflowY = localIsMobileMenuVisible ? "hidden" : "auto"
  }

  // Keep body scroll lock in sync with menu state; always restore on unmount
  useEffect(() => {
    document.body.style.overflowY = isMobileMenuVisible ? "hidden" : "auto"
    return () => {
      document.body.style.overflowY = "auto"
    }
  }, [isMobileMenuVisible])

  // Close mobile menu on route change to ensure scroll is restored after navigation
  useEffect(() => {
    if (previousPathnameRef.current === pathname) {
      return
    }

    previousPathnameRef.current = pathname
    setIsMobileMenuVisible(false)
  }, [pathname])

  const [isRebrushSticky, setIsRebrushSticky] = useState(false)

  useEffect(() => {
    if (!useRebrushLayout) {
      return
    }

    const desktopMediaQuery = window.matchMedia("(min-width: 1024px)")
    const desktopStickyEnterScrollY = 64
    const desktopStickyExitScrollY = 24
    const mobileStickyEnterScrollY = 64
    const mobileStickyExitScrollY = 24
    let animationFrame: number | undefined

    const updateStickyState = () => {
      if (animationFrame !== undefined) {
        return
      }

      animationFrame = window.requestAnimationFrame(() => {
        const shouldUseDesktopSticky = desktopMediaQuery.matches
        const scrollY = window.scrollY

        setIsRebrushSticky((currentSticky) => {
          if (shouldUseDesktopSticky) {
            if (!currentSticky && scrollY >= desktopStickyEnterScrollY) {
              return true
            }
            if (currentSticky && scrollY <= desktopStickyExitScrollY) {
              return false
            }
            return currentSticky
          }

          if (!currentSticky && scrollY >= mobileStickyEnterScrollY) {
            return true
          }
          if (currentSticky && scrollY <= mobileStickyExitScrollY) {
            return false
          }
          return currentSticky
        })

        animationFrame = undefined
      })
    }

    updateStickyState()
    window.addEventListener("scroll", updateStickyState, { passive: true })
    desktopMediaQuery.addEventListener("change", updateStickyState)

    return () => {
      window.removeEventListener("scroll", updateStickyState)
      desktopMediaQuery.removeEventListener("change", updateStickyState)
      if (animationFrame !== undefined) {
        window.cancelAnimationFrame(animationFrame)
      }
    }
  }, [useRebrushLayout])

  if (useRebrushLayout) {
    return (
      <>
        <div className="h-[161px] lg:hidden" aria-hidden="true" />
        <div
          className={cn("fixed top-0 right-0 left-0 z-151 w-full bg-white", {
            "h-screen overflow-y-auto pb-36": isMobileMenuVisible,
          })}
        >
          <div
            className={cn(
              "h-10 overflow-hidden bg-[#2D4B55] text-white lg:h-9",
              isRebrushSticky && "h-0 opacity-0 lg:h-0 lg:opacity-0"
            )}
            style={{
              transition:
                "height .18s cubic-bezier(.4,0,.2,1), padding .18s cubic-bezier(.4,0,.2,1), opacity .12s linear",
            }}
          >
            <div
              className={cn(
                "mx-auto flex h-10 max-w-7xl items-center px-3 lg:h-9 lg:justify-end lg:gap-8 lg:px-8",
                showRebrushCustomerToggle ? "justify-between gap-8" : "justify-end"
              )}
            >
              <StoryblokServicePhone blok={blok} sbLocale={sbLocale} oci={effectiveOci} variant="rebrush" />
              {showRebrushCustomerToggle && (
                <StoryblokPrivateCustomerToggle
                  isB2BCustomer={effectiveIsB2BCustomer}
                  userData={effectiveUserData}
                  variant="rebrush"
                />
              )}
            </div>
          </div>

          <div className="bg-white">
            <div
              className={cn(
                "mx-auto flex max-w-7xl items-center gap-4 px-3 pt-4 pb-3 lg:gap-[72px] lg:px-8",
                isRebrushSticky ? "lg:py-3" : "lg:py-4"
              )}
            >
              <HeaderLogo
                logo={blok.logo}
                subtext={blok.subtext}
                sbLocale={sbLocale}
                className={cn("h-[41px] w-[152px]", isRebrushSticky ? "lg:h-11 lg:w-[164px]" : "lg:h-12 lg:w-[180px]")}
                logoClassName={cn("max-h-[41px]", isRebrushSticky ? "lg:max-h-11" : "lg:max-h-12")}
                hideSubtext
                customerLogoUrl={effectiveCustomerLogoUrl}
                variant="rebrush"
              />
              <div className="hidden h-10 min-w-0 flex-1 lg:block">
                <StoryblokSearchBox isoLocale={isoLocale} variant="rebrush" />
              </div>
              <div className="ml-auto flex h-10 shrink-0 items-center gap-2 lg:gap-4">
                <StoryblokAccountPopupClient
                  blok={blok}
                  userData={effectiveUserData}
                  sbLocale={sbLocale}
                  disablePopup={disableAccountPopup}
                  variant="rebrush"
                />
                <StoryblokShoppingBasketPopup
                  isoLocale={isoLocale}
                  blok={blok}
                  applicationSettings={applicationSettings}
                  isB2B={effectiveIsB2BFallback}
                  isAnonymous={effectiveIsAnonymous}
                  userData={effectiveUserData}
                  variant="rebrush"
                  disablePopup
                />
                <StoryblokSwissLocaleSwitch isoLocale={isoLocale} variant="rebrush" />
                <StoryblokBelgianLocaleSwitch isoLocale={isoLocale} variant="rebrush" />
                <button
                  aria-label={t("header.menu")}
                  onClick={onMobileMenuClick}
                  className="flex size-10 shrink-0 items-center justify-center rounded-[4px] text-[#303030] hover:bg-[#F8F8F8] focus-visible:ring-2 focus-visible:ring-ub-hlblue focus-visible:outline-hidden lg:hidden"
                >
                  <AlignLeftIcon className={"size-6"} strokeWidth={1.6} />
                </button>
              </div>
            </div>
          </div>

          <div className="flex h-[52px] items-center px-3 pt-1 pb-2 shadow-[inset_0_-1px_0_#EEEEEE] lg:hidden">
            <div className="h-10 min-w-0 flex-1">
              <StoryblokSearchBox isoLocale={isoLocale} variant="rebrush" />
            </div>
          </div>

          <StoryblokMenuBar
            isB2BCustomer={effectiveIsB2BFallback}
            displayIsB2BCustomer={effectiveIsB2BCustomer}
            blok={blok}
            categories={effectiveCategories}
            navigation={navigation}
            toolNavigation={toolNavigation}
            isMobileVisible={isMobileMenuVisible}
            userData={effectiveUserData}
            sbLocale={sbLocale}
            oci={effectiveOci}
            labelAllCategories={t("header.all_categories")}
            variant="rebrush"
          />
        </div>
      </>
    )
  }

  return (
    /*Why w-screen? See:
     https://github.com/radix-ui/primitives/issues/1496 and
     https://www.reddit.com/r/reactjs/comments/1fjcwkh/comment/lnt3a0j/?utm_source=share&utm_medium=web3x&utm_name=web3xcss&utm_term=1&utm_content=share_button*/
    <div
      className={cn("fixed z-151 w-screen bg-white", {
        "h-screen overflow-y-auto pb-36": isMobileMenuVisible,
      })}
    >
      <div
        className={cn(
          `
            mx-auto flex h-14 max-w-7xl items-start border-b border-gray-200 px-4 py-2
            sm:px-6
            lg:h-[90px] lg:border-0 lg:px-8
          `,
          {
            "hidden lg:block": isMobileSearchVisible,
          }
        )}
      >
        <div className="flex w-full items-center gap-4 lg:items-start">
          <HeaderLogo
            logo={blok.logo}
            subtext={blok.subtext}
            sbLocale={sbLocale}
            className={""}
            customerLogoUrl={effectiveCustomerLogoUrl}
          />
          <div className={cn("flex grow flex-col justify-center gap-2")}>
            <div className={"hidden h-7 flex-row border-b border-gray-200 transition-all lg:flex"}>
              <div className={"flex w-full flex-row items-center justify-between gap-1 pb-1"}>
                <div className="mr-2 flex grow flex-row items-center justify-between gap-2">
                  {effectiveOci ? (
                    <>
                      {blok.benefit_oci_1 && (
                        <StoryblokValueProposition text={blok.benefit_oci_1} link={blok.benefit_link_oci_1?.url} />
                      )}
                      {blok.benefit_oci_2 && (
                        <StoryblokValueProposition
                          text={blok.benefit_oci_2}
                          link={blok.benefit_link_oci_2?.url}
                          className={"hidden lg:flex"}
                        />
                      )}
                      {blok.benefit_oci_3 && (
                        <StoryblokValueProposition
                          text={blok.benefit_oci_3}
                          link={blok.benefit_link_oci_3?.url}
                          className="hidden xl:flex"
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {effectiveIsB2BFallback ? (
                        <>
                          {blok.benefit_b2b_1 && (
                            <StoryblokValueProposition text={blok.benefit_b2b_1} link={blok.benefit_link_b2b_1?.url} />
                          )}
                          {blok.benefit_b2b_2 && (
                            <StoryblokValueProposition
                              text={blok.benefit_b2b_2}
                              link={blok.benefit_link_b2b_2?.url}
                              className={"hidden lg:flex"}
                            />
                          )}
                          {blok.benefit_b2b_3 && (
                            <StoryblokValueProposition
                              text={blok.benefit_b2b_3}
                              link={blok.benefit_link_b2b_3?.url}
                              className="hidden xl:flex"
                            />
                          )}
                        </>
                      ) : (
                        <>
                          {blok.benefit_b2c_1 && (
                            <StoryblokValueProposition text={blok.benefit_b2c_1} link={blok.benefit_link_b2c_1?.url} />
                          )}
                          {blok.benefit_b2c_2 && (
                            <StoryblokValueProposition
                              text={blok.benefit_b2c_2}
                              link={blok.benefit_link_b2c_2?.url}
                              className={"hidden lg:flex"}
                            />
                          )}
                          {blok.benefit_b2c_3 && (
                            <StoryblokValueProposition
                              text={blok.benefit_b2c_3}
                              link={blok.benefit_link_b2c_3?.url}
                              className="hidden xl:flex"
                            />
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
                <div className={"flex items-center justify-between gap-1 lg:min-w-96 xl:gap-2"}>
                  <StoryblokServicePhone blok={blok} sbLocale={sbLocale} oci={effectiveOci} />
                  {!effectiveOci && (
                    <StoryblokPrivateCustomerToggle
                      isB2BCustomer={effectiveIsB2BCustomer}
                      userData={effectiveUserData}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="flex h-9 grow flex-row items-center justify-end xl:gap-2">
              <div className="size-full grow max-lg:hidden">
                <StoryblokSearchBox isoLocale={isoLocale} />
              </div>
              <div className={"flex grow items-center justify-between max-lg:max-w-40 lg:min-w-96 xl:gap-2"}>
                <button
                  aria-label={t("header.search_placeholder")}
                  onClick={() => setIsMobileSearchVisible(true)}
                  className="flex h-full flex-row items-center justify-center text-gray-600 lg:hidden"
                >
                  <SearchIcon className={"h-[20px] text-black"} />
                </button>
                <StoryblokAccountPopupClient
                  blok={blok}
                  userData={effectiveUserData}
                  sbLocale={sbLocale}
                  disablePopup={disableAccountPopup}
                />
                <StoryblokShoppingBasketPopup
                  isoLocale={isoLocale}
                  blok={blok}
                  applicationSettings={applicationSettings}
                  isB2B={effectiveIsB2BFallback}
                  isAnonymous={effectiveIsAnonymous}
                  userData={effectiveUserData}
                />
                <StoryblokSwissLocaleSwitch isoLocale={isoLocale} />
                <StoryblokBelgianLocaleSwitch isoLocale={isoLocale} />
                <button
                  aria-label={t("header.menu")}
                  onClick={onMobileMenuClick}
                  className="flex h-full flex-row items-center justify-center font-semibold text-gray-600 lg:hidden"
                >
                  <AlignLeftIcon className={"h-[20px] text-black"} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isMobileSearchVisible && (
        <div className="p-3.5 lg:hidden">
          <StoryblokSearchBox isoLocale={isoLocale} onClose={() => setIsMobileSearchVisible(false)} />
        </div>
      )}

      <StoryblokMenuBar
        isB2BCustomer={effectiveIsB2BFallback}
        displayIsB2BCustomer={effectiveIsB2BCustomer}
        blok={blok}
        categories={effectiveCategories}
        navigation={navigation}
        isMobileVisible={isMobileMenuVisible}
        userData={effectiveUserData}
        sbLocale={sbLocale}
        oci={effectiveOci}
        labelAllCategories={t("header.all_categories")}
        categoriesLoading={categoriesLoading}
      />
    </div>
  )
}
